"use client";

import React, { useState, useMemo } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Copy,
  Check,
  RotateCcw,
  Sliders,
  Cpu,
  Flame,
  Maximize2,
  Code2,
} from "lucide-react";

interface PrinterProfile {
  id: string;
  name: string;
  maxX: number;
  maxY: number;
  maxZ: number;
  maxNozzleTemp: number;
  maxBedTemp: number;
}

interface MaterialProfile {
  id: string;
  name: string;
  minNozzle: number;
  maxNozzle: number;
  minBed: number;
  maxBed: number;
}

const PRINTERS: Record<string, PrinterProfile> = {
  bambu_p1s: {
    id: "bambu_p1s",
    name: "Bambu Lab P1S / X1C",
    maxX: 256,
    maxY: 256,
    maxZ: 256,
    maxNozzleTemp: 300,
    maxBedTemp: 100,
  },
  prusa_mk4: {
    id: "prusa_mk4",
    name: "Prusa MK4 (PrusaLink)",
    maxX: 250,
    maxY: 210,
    maxZ: 220,
    maxNozzleTemp: 300,
    maxBedTemp: 120,
  },
  voron_24: {
    id: "voron_24",
    name: "Voron 2.4 (Klipper / Moonraker)",
    maxX: 300,
    maxY: 300,
    maxZ: 300,
    maxNozzleTemp: 350,
    maxBedTemp: 115,
  },
  ender3_marlin: {
    id: "ender3_marlin",
    name: "Creality Ender 3 (USB / Marlin)",
    maxX: 220,
    maxY: 220,
    maxZ: 250,
    maxNozzleTemp: 260,
    maxBedTemp: 100,
  },
};

const MATERIALS: Record<string, MaterialProfile> = {
  pla: {
    id: "pla",
    name: "PLA (Standard)",
    minNozzle: 190,
    maxNozzle: 225,
    minBed: 45,
    maxBed: 65,
  },
  petg: {
    id: "petg",
    name: "PETG (Zäh & UV-stabil)",
    minNozzle: 230,
    maxNozzle: 255,
    minBed: 70,
    maxBed: 85,
  },
  abs: {
    id: "abs",
    name: "ABS / ASA (Hitzebeständig)",
    minNozzle: 240,
    maxNozzle: 270,
    minBed: 90,
    maxBed: 110,
  },
  pc: {
    id: "pc",
    name: "PC / Carbon (Hochleistung)",
    minNozzle: 270,
    maxNozzle: 310,
    minBed: 100,
    maxBed: 120,
  },
};

const DANGEROUS_COMMANDS = [
  { code: "M997", desc: "Firmware-Flash ausführen (Gefahr von Brick)" },
  { code: "M502", desc: "Factory Reset / Löschen kalibrierter Offsets" },
  { code: "M851", desc: "Überschreiben des Z-Probe Offsets (Crash-Risiko)" },
  { code: "M106 S255", desc: "Lüfter ungedrosselt ohne Soft-Start" },
  { code: "M206", desc: "Verschiebung des Maschinen-Nullpunkts" },
];

const PRESETS = [
  {
    name: "Sicherer Prototyp",
    printer: "bambu_p1s",
    material: "pla",
    nozzleTemp: 215,
    bedTemp: 55,
    dimX: 110,
    dimY: 75,
    dimZ: 42,
    gcode: "G28 ; Home all\nG29 ; Bed mesh auto-level\nM109 S215 ; Set target nozzle\nM190 S55 ; Set target bed",
    humanConfirmed: true,
  },
  {
    name: "Thermische Grenzüberschreitung",
    printer: "ender3_marlin",
    material: "pc",
    nozzleTemp: 310,
    bedTemp: 110,
    dimX: 80,
    dimY: 80,
    dimZ: 50,
    gcode: "M104 S310 ; Nozzle 310C (Hardware-Limit 260C überschritten!)\nM140 S110 ; Bed 110C\nG28",
    humanConfirmed: true,
  },
  {
    name: "Bauraum-Kollision",
    printer: "prusa_mk4",
    material: "petg",
    nozzleTemp: 240,
    bedTemp: 75,
    dimX: 275,
    dimY: 180,
    dimZ: 90,
    gcode: "G28\nG1 X275 Y180 Z10 F3000 ; X überragt Druckbett (250mm Max!)",
    humanConfirmed: true,
  },
  {
    name: "Bösartiges G-Code Kommando",
    printer: "voron_24",
    material: "abs",
    nozzleTemp: 250,
    bedTemp: 100,
    dimX: 100,
    dimY: 100,
    dimZ: 50,
    gcode: "G28\nM997 ; Unautorisierter Firmware-Flash Versuch!\nM502 ; Werksreset erzwingen",
    humanConfirmed: false,
  },
];

export default function PreflightAuditorPlayground() {
  const [presetIndex, setPresetIndex] = useState(0);
  const [printerKey, setPrinterKey] = useState("bambu_p1s");
  const [materialKey, setMaterialKey] = useState("pla");
  const [nozzleTemp, setNozzleTemp] = useState(215);
  const [bedTemp, setBedTemp] = useState(55);
  const [dimX, setDimX] = useState(110);
  const [dimY, setDimY] = useState(75);
  const [dimZ, setDimZ] = useState(42);
  const [gcode, setGcode] = useState(PRESETS[0].gcode);
  const [humanConfirmed, setHumanConfirmed] = useState(true);
  const [copied, setCopied] = useState(false);

  const applyPreset = (idx: number) => {
    setPresetIndex(idx);
    const p = PRESETS[idx];
    setPrinterKey(p.printer);
    setMaterialKey(p.material);
    setNozzleTemp(p.nozzleTemp);
    setBedTemp(p.bedTemp);
    setDimX(p.dimX);
    setDimY(p.dimY);
    setDimZ(p.dimZ);
    setGcode(p.gcode);
    setHumanConfirmed(p.humanConfirmed);
  };

  const printer = PRINTERS[printerKey];
  const material = MATERIALS[materialKey];

  // Deterministic Safety Evaluation
  const audit = useMemo(() => {
    const checks: {
      category: string;
      label: string;
      status: "pass" | "warn" | "fail";
      reason: string;
    }[] = [];

    let hasFail = false;
    let hasWarn = false;

    // 1. Hardware Thermal Limits
    if (nozzleTemp > printer.maxNozzleTemp) {
      checks.push({
        category: "Hardware-Sicherheit",
        label: "Hotend-Temperaturgrenze",
        status: "fail",
        reason: `Solltemperatur (${nozzleTemp}°C) überschreitet physisches Hardware-Limit (${printer.maxNozzleTemp}°C). Brandgefahr!`,
      });
      hasFail = true;
    } else if (nozzleTemp > material.maxNozzle) {
      checks.push({
        category: "Material-Fenster",
        label: "Hotend zu heiß für Material",
        status: "warn",
        reason: `Solltemperatur (${nozzleTemp}°C) liegt über dem empfohlenen ${material.name}-Fenster (max ${material.maxNozzle}°C). Zersetzungsrisiko.`,
      });
      hasWarn = true;
    } else if (nozzleTemp < material.minNozzle) {
      checks.push({
        category: "Material-Fenster",
        label: "Hotend zu kalt für Material",
        status: "warn",
        reason: `Solltemperatur (${nozzleTemp}°C) liegt unter Schmelztemperatur (min ${material.minNozzle}°C). Extruder-Verstopfung droht.`,
      });
      hasWarn = true;
    } else {
      checks.push({
        category: "Hardware-Sicherheit",
        label: "Hotend-Temperatur im Zielbereich",
        status: "pass",
        reason: `${nozzleTemp}°C liegt sicher unter Hardware-Limit (${printer.maxNozzleTemp}°C) und im ${material.name}-Fenster.`,
      });
    }

    // 2. Bed Thermal Limits
    if (bedTemp > printer.maxBedTemp) {
      checks.push({
        category: "Hardware-Sicherheit",
        label: "Druckbett-Temperaturgrenze",
        status: "fail",
        reason: `Betttemperatur (${bedTemp}°C) überschreitet Maximalwert der Bauplatte (${printer.maxBedTemp}°C).`,
      });
      hasFail = true;
    } else if (bedTemp > material.maxBed + 10) {
      checks.push({
        category: "Material-Fenster",
        label: "Druckbett überhitzt",
        status: "warn",
        reason: `${bedTemp}°C liegt deutlich über dem Glasübergangspunkt (${material.maxBed}°C). Bauteil-Verformung möglich.`,
      });
      hasWarn = true;
    } else {
      checks.push({
        category: "Hardware-Sicherheit",
        label: "Druckbett-Temperatur zulässig",
        status: "pass",
        reason: `${bedTemp}°C liegt im sicheren Betriebsbereich (Maximal: ${printer.maxBedTemp}°C).`,
      });
    }

    // 3. Dimensions & Build Volume Collision
    const fitsX = dimX <= printer.maxX;
    const fitsY = dimY <= printer.maxY;
    const fitsZ = dimZ <= printer.maxZ;

    if (!fitsX || !fitsY || !fitsZ) {
      const violations: string[] = [];
      if (!fitsX) violations.push(`X: ${dimX}mm > ${printer.maxX}mm`);
      if (!fitsY) violations.push(`Y: ${dimY}mm > ${printer.maxY}mm`);
      if (!fitsZ) violations.push(`Z: ${dimZ}mm > ${printer.maxZ}mm`);
      checks.push({
        category: "Bauraum & Kinematik",
        label: "Bauteil überragt Druckbett",
        status: "fail",
        reason: `Modellmaße (${violations.join(", ")}) überschreiten das Druckvolumen von ${printer.name}. Kollisionsgefahr mit Achsen!`,
      });
      hasFail = true;
    } else {
      checks.push({
        category: "Bauraum & Kinematik",
        label: "Bauraum-Validierung erfolgreich",
        status: "pass",
        reason: `Modell (${dimX}×${dimY}×${dimZ} mm) passt vollständig in das Bauvolumen (${printer.maxX}×${printer.maxY}×${printer.maxZ} mm).`,
      });
    }

    // 4. Dangerous G-Code Inspection
    const foundBadCommands: { code: string; desc: string }[] = [];
    for (const cmd of DANGEROUS_COMMANDS) {
      if (gcode.toUpperCase().includes(cmd.code)) {
        foundBadCommands.push(cmd);
      }
    }

    if (foundBadCommands.length > 0) {
      checks.push({
        category: "Befehls-Filter (Sanitizer)",
        label: "Gefährliche G-Codes entdeckt",
        status: "fail",
        reason: `Blockierte Befehle: ${foundBadCommands.map((c) => `${c.code} (${c.desc})`).join(", ")}. Direkte Ausführung unterbunden.`,
      });
      hasFail = true;
    } else {
      checks.push({
        category: "Befehls-Filter (Sanitizer)",
        label: "G-Code Befehlssatz unbedenklich",
        status: "pass",
        reason: "Keine destruktiven Firmware- oder Kalibrierungs-Overrides in der Startsequenz erkannt.",
      });
    }

    // 5. Human-in-the-Loop Gate (Elicitation)
    if (!humanConfirmed) {
      checks.push({
        category: "Menschliche Freigabe (HITL)",
        label: "Druckbett-Freigabe fehlt",
        status: "warn",
        reason: "MCP-Elicitation aktiv: Heizpatronen und Achsen werden erst nach interaktiver Bestätigung des freien Druckbetts bestromt.",
      });
      hasWarn = true;
    } else {
      checks.push({
        category: "Menschliche Freigabe (HITL)",
        label: "Druckbett durch Bediener quittiert",
        status: "pass",
        reason: "Explizite Sicherheitsbestätigung liegt vor. Druckjob darf physisch gestartet werden.",
      });
    }

    const overallStatus: "pass" | "warn" | "fail" = hasFail
      ? "fail"
      : hasWarn
      ? "warn"
      : "pass";

    return {
      checks,
      overallStatus,
    };
  }, [printer, material, nozzleTemp, bedTemp, dimX, dimY, dimZ, gcode, humanConfirmed]);

  // Generate simulated MCP JSON-RPC Payload
  const mcpJson = useMemo(() => {
    if (audit.overallStatus === "fail") {
      const failedReasons = audit.checks
        .filter((c) => c.status === "fail")
        .map((c) => c.reason);
      return JSON.stringify(
        {
          jsonrpc: "2.0",
          id: "req_3d_print_01",
          error: {
            code: -32001,
            message: "PREFLIGHT_SAFETY_GATE_REJECTED",
            data: {
              printer: printer.id,
              violations: failedReasons,
              suggested_agent_action:
                "Korrigiere Solltemperaturen oder skaliere das Modell mit auto_orient_model() / split_mesh_to_fit()",
            },
          },
        },
        null,
        2
      );
    }

    return JSON.stringify(
      {
        jsonrpc: "2.0",
        id: "req_3d_print_01",
        result: {
          status: "QUEUED_AND_DISPATCHED",
          printer: printer.id,
          target_temperatures: {
            extruder: nozzleTemp,
            bed: bedTemp,
          },
          bounding_box: { x: dimX, y: dimY, z: dimZ },
          material: material.id,
          hitl_confirmed: humanConfirmed,
          safety_policy: "strict_preflight_v2",
          telemetry_active: true,
        },
      },
      null,
      2
    );
  }, [audit, printer, material, nozzleTemp, bedTemp, dimX, dimY, dimZ, humanConfirmed]);

  const handleCopy = () => {
    navigator.clipboard?.writeText(mcpJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6 text-neutral-900 dark:text-neutral-100 min-h-[640px]">
      {/* Preset Selector */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          Szenario-Presets zur Demonstration:
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PRESETS.map((p, idx) => (
            <button
              key={p.name}
              type="button"
              onClick={() => applyPreset(idx)}
              className={`min-h-[44px] rounded-lg px-3 py-2 text-xs font-medium transition-all text-left flex flex-col justify-center border ${
                presetIndex === idx
                  ? "border-purple-500 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-semibold shadow-xs"
                  : "border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
              }`}
            >
              <span className="truncate">{p.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid: Controls vs Live Audit Results */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Controls Column (5 cols) */}
        <div className="lg:col-span-5 flex flex-col gap-4 rounded-xl border border-neutral-200/90 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/40 p-4">
          <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-800 pb-2 text-xs font-bold text-neutral-700 dark:text-neutral-300">
            <Sliders className="h-4 w-4 text-purple-500" />
            <span>Agenten-Parameter & Druckprofil</span>
          </div>

          {/* Printer Selection */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="printer-select"
              className="text-xs font-medium text-neutral-600 dark:text-neutral-400"
            >
              Ziel-Drucker (Hardware):
            </label>
            <select
              id="printer-select"
              value={printerKey}
              onChange={(e) => setPrinterKey(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-xs font-medium text-neutral-900 dark:text-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
            >
              {Object.values(PRINTERS).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.maxX}×{p.maxY}×{p.maxZ} mm)
                </option>
              ))}
            </select>
          </div>

          {/* Material Selection */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="material-select"
              className="text-xs font-medium text-neutral-600 dark:text-neutral-400"
            >
              Filament-Typ:
            </label>
            <select
              id="material-select"
              value={materialKey}
              onChange={(e) => setMaterialKey(e.target.value)}
              className="min-h-[44px] w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-xs font-medium text-neutral-900 dark:text-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
            >
              {Object.values(MATERIALS).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.minNozzle}–{m.maxNozzle}°C)
                </option>
              ))}
            </select>
          </div>

          {/* Temperatures */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="nozzle-temp"
                className="text-xs font-medium text-neutral-600 dark:text-neutral-400 flex items-center justify-between"
              >
                <span>Nozzle (°C):</span>
                <span className="font-mono text-purple-600 dark:text-purple-400 font-bold">
                  {nozzleTemp}°C
                </span>
              </label>
              <input
                id="nozzle-temp"
                type="number"
                min={150}
                max={380}
                value={nozzleTemp}
                onChange={(e) => setNozzleTemp(Number(e.target.value))}
                className="min-h-[44px] w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-xs font-mono font-medium text-neutral-900 dark:text-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="bed-temp"
                className="text-xs font-medium text-neutral-600 dark:text-neutral-400 flex items-center justify-between"
              >
                <span>Bett (°C):</span>
                <span className="font-mono text-purple-600 dark:text-purple-400 font-bold">
                  {bedTemp}°C
                </span>
              </label>
              <input
                id="bed-temp"
                type="number"
                min={30}
                max={150}
                value={bedTemp}
                onChange={(e) => setBedTemp(Number(e.target.value))}
                className="min-h-[44px] w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-xs font-mono font-medium text-neutral-900 dark:text-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Dimensions */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
              Bounding Box (X × Y × Z in mm):
            </label>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="number"
                aria-label="Breite X in mm"
                value={dimX}
                onChange={(e) => setDimX(Number(e.target.value))}
                className="min-h-[44px] rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 py-2 text-center text-xs font-mono font-medium text-neutral-900 dark:text-neutral-100"
                placeholder="X"
              />
              <input
                type="number"
                aria-label="Tiefe Y in mm"
                value={dimY}
                onChange={(e) => setDimY(Number(e.target.value))}
                className="min-h-[44px] rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 py-2 text-center text-xs font-mono font-medium text-neutral-900 dark:text-neutral-100"
                placeholder="Y"
              />
              <input
                type="number"
                aria-label="Höhe Z in mm"
                value={dimZ}
                onChange={(e) => setDimZ(Number(e.target.value))}
                className="min-h-[44px] rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 py-2 text-center text-xs font-mono font-medium text-neutral-900 dark:text-neutral-100"
                placeholder="Z"
              />
            </div>
          </div>

          {/* G-Code Start Preview */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="gcode-input"
              className="text-xs font-medium text-neutral-600 dark:text-neutral-400"
            >
              G-Code Start-Sequenz:
            </label>
            <textarea
              id="gcode-input"
              rows={3}
              value={gcode}
              onChange={(e) => setGcode(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-2 font-mono text-[11px] leading-relaxed text-neutral-900 dark:text-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Human Confirmation */}
          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={humanConfirmed}
              onChange={(e) => setHumanConfirmed(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              Bediener hat freie Druckplatte bestätigt (HITL Elicitation)
            </span>
          </label>
        </div>

        {/* Audit Results & JSON RPC Column (7 cols) */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          {/* Header Status Banner */}
          <div
            className={`flex items-center justify-between rounded-xl border p-4 transition-colors ${
              audit.overallStatus === "pass"
                ? "border-emerald-500/40 bg-emerald-50/80 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-100"
                : audit.overallStatus === "warn"
                ? "border-amber-500/40 bg-amber-50/80 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100"
                : "border-rose-500/40 bg-rose-50/80 dark:bg-rose-950/20 text-rose-900 dark:text-rose-100"
            }`}
          >
            <div className="flex items-center gap-3">
              {audit.overallStatus === "pass" && (
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
              )}
              {audit.overallStatus === "warn" && (
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" />
              )}
              {audit.overallStatus === "fail" && (
                <XCircle className="h-6 w-6 text-rose-600 dark:text-rose-400 shrink-0" />
              )}
              <div>
                <span className="font-heading text-sm font-bold block">
                  {audit.overallStatus === "pass" && "GATE PASS: DRUCK FREIGEGEBEN"}
                  {audit.overallStatus === "warn" && "WARNUNG: BESTÄTIGUNG ERFORDERLICH"}
                  {audit.overallStatus === "fail" && "GATE BLOCKIERT: SICHERHEITSRISIKO"}
                </span>
                <span className="text-xs opacity-90 block">
                  {audit.overallStatus === "pass" &&
                    "Alle thermischen, geometrischen und Befehls-Schwellenwerte eingehalten."}
                  {audit.overallStatus === "warn" &&
                    "Erhöhte Aufmerksamkeit nötig. Elicitation-Prüfung oder Randparameter."}
                  {audit.overallStatus === "fail" &&
                    "Kritische Grenzüberschreitung. Server verweigert Ausführung an der Hardware."}
                </span>
              </div>
            </div>
            <span className="font-mono text-xs font-black uppercase px-2 py-1 rounded bg-black/5 dark:bg-white/10 shrink-0">
              {audit.overallStatus}
            </span>
          </div>

          {/* Audit Rule Checks List */}
          <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/60 p-4">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Preflight Audit Matrix (5 Sicherheitskriterien):
            </span>
            <div className="flex flex-col gap-2">
              {audit.checks.map((chk, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 rounded-lg border border-neutral-100 dark:border-neutral-800/80 bg-neutral-50/70 dark:bg-neutral-850/40 p-2.5 text-xs"
                >
                  <span className="mt-0.5 shrink-0">
                    {chk.status === "pass" && (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    )}
                    {chk.status === "warn" && (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                    {chk.status === "fail" && (
                      <XCircle className="h-4 w-4 text-rose-500" />
                    )}
                  </span>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-semibold text-neutral-800 dark:text-neutral-200 truncate">
                        {chk.label}
                      </span>
                      <span className="text-[10px] font-mono text-neutral-400 shrink-0">
                        {chk.category}
                      </span>
                    </div>
                    <span className="text-[11px] text-neutral-600 dark:text-neutral-400 mt-0.5 leading-snug">
                      {chk.reason}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Resulting MCP JSON-RPC Payload */}
          <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-950 p-3.5 text-white">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <div className="flex items-center gap-2 font-mono text-xs text-neutral-300">
                <Code2 className="h-3.5 w-3.5 text-purple-400" />
                <span>Resultierender MCP JSON-RPC Wire Payload</span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 rounded bg-neutral-800 hover:bg-neutral-700 px-2 py-1 text-[11px] font-medium text-neutral-200 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-400" />
                    <span>Kopiert</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Kopieren</span>
                  </>
                )}
              </button>
            </div>
            <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-emerald-400 max-h-48 p-1">
              <code>{mcpJson}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
